/**
 * GuestGuard — protects routes that should only be accessible when the user
 * is NOT authenticated (login, signup, forgot-password, etc.).
 *
 * Behaviour:
 *   - While /me/ is in flight      → renders <FullPageSkeleton />.
 *   - On success (user found)      → redirects to /app (already logged in).
 *   - On error / no session        → renders <Outlet /> (allow access).
 *
 * This guard does NOT need to write to the Redux slice — AuthGuard handles
 * that for protected routes.  GuestGuard only reads the query result to
 * decide whether to allow or redirect.
 */
import { Outlet, Navigate } from 'react-router-dom';

import { useMeQuery } from './api/authApi';
import { FullPageSkeleton } from '@/shared/components/FullPageSkeleton';

export function GuestGuard() {
  const { data: user, isLoading, isSuccess } = useMeQuery();

  if (isLoading) {
    return <FullPageSkeleton />;
  }

  // Already authenticated — bounce to the app shell.
  if (isSuccess && user) {
    return <Navigate to="/app" replace />;
  }

  // No valid session — render the auth screen (login, signup, etc.).
  return <Outlet />;
}

export default GuestGuard;
